import { FC, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { ButtonGroup, Button, Box } from "@mui/material";
import CheckIcon from '@mui/icons-material/Check';

import { useUserParams } from '../../../context/user-params-context';
import { ICriteria } from "./common/types";
import { DeepPartial } from "../../../types/utility";
import { useDate } from '../../../utility/useDate';
import { usePreferences } from '../../../utility/usePreferences';
import { mutateDate, getToday } from './common/date-utils';
import { DateTime } from 'luxon';

interface DayShiftOption {
    value: number;
    label: string;
    default?: boolean;
}

interface IDayShiftConfig {
    options?: DeepPartial<DayShiftOption>[];
}

export const DayShiftSelector: FC<ICriteria<IDayShiftConfig>> = ({ config }) => {
    const { userParams, setUserParams, clearParams } = useUserParams();
    const { timeZone } = usePreferences();
    const { localizeDate } = useDate();
    const { t } = useTranslation();

    // Default options if no config options are provided
    const defaultOptions: DayShiftOption[] = [
        { value: -1, label: t('common.yesterday') },
        { value: 0, label: t('common.today'), default: true },
        { value: 1, label: t('common.tomorrow') }
    ];

    const options: DayShiftOption[] = config?.options
        ? config.options
            .filter((opt): opt is DeepPartial<DayShiftOption> => opt !== undefined)
            .map(opt => ({
                value: typeof opt.value === 'number' ? opt.value : 0,
                label: opt.label ?? '',
                default: opt.default
            }))
        : defaultOptions;

    const defaultOption = options.find(opt => opt.default === true) || options[1];

    // Calculate selected value based on current start/end dates
    const getCurrentDayOffset = (): number => {
        if (!userParams["@u_startDate"]) return defaultOption.value;

        try {
            // TODO: DD - get rid of luxton
            const currentStartDate = DateTime.fromISO(userParams["@u_startDate"]).setZone(timeZone).startOf('day');
            const today = getToday(timeZone);
            const diffDays = currentStartDate.diff(today, 'days').days;
            return options.some(opt => opt.value === diffDays) ? diffDays : defaultOption.value;
        } catch (e) {
            return defaultOption.value;
        }
    };

    const selectedValue = getCurrentDayOffset();

    useEffect(() => {
        // Get today's date in the user's timezone and adjust by the offset
        const today = getToday(timeZone);
        const baseDate = mutateDate(today, { add: { days: defaultOption.value } });

        // Set both start and end dates based on the selected day
        const startOfDay = localizeDate(baseDate.toJSDate()).startOf('day').toISO();
        const endOfDay = localizeDate(baseDate.toJSDate()).endOf('day').toISO();

        setUserParams({
            "@u_startDate": startOfDay,
            "@u_endDate": endOfDay
        });

        return () => {
            clearParams(['@u_startDate', '@u_endDate']);
        };
    }, []);

    const handleChange = (value: number) => {
        // Get today's date in the user's timezone and adjust by the offset
        const today = getToday(timeZone);
        const baseDate = mutateDate(today, { add: { days: value } });

        // Update both start and end dates based on the selected day
        const startOfDay = localizeDate(baseDate.toJSDate()).startOf('day').toISO();
        const endOfDay = localizeDate(baseDate.toJSDate()).endOf('day').toISO();

        setUserParams({
            "@u_startDate": startOfDay,
            "@u_endDate": endOfDay
        });
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, mb: 2, mt: 2 }}>
            <ButtonGroup
                variant="outlined"
                aria-label="date selection button group"
                disableElevation
                sx={{
                    '& .MuiButton-outlined': {
                        borderColor: '#d0d0d0',
                        color: 'rgba(0, 0, 0, 0.87)',
                    },
                    '& .MuiButton-root:hover': {
                        borderColor: '#a0a0a0',
                        backgroundColor: 'transparent',
                        zIndex: 1,
                    }
                }}
            >
                {options.map((option) => (
                    <Button
                        key={option.value}
                        onClick={() => handleChange(option.value)}
                        variant="outlined"
                        startIcon={option.value === selectedValue ? <CheckIcon fontSize="small" /> : null}
                        sx={{
                            textTransform: 'none',
                            minWidth: '120px',
                            fontWeight: option.value === selectedValue ? 500 : 400,
                        }}
                    >
                        {option.label}
                    </Button>
                ))}
            </ButtonGroup>
        </Box>
    );
}; 